import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { supabase } from '../lib/supabase'

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatAUD(amount) {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount)
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-AU', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  })
}

function InfoItem({ label, value }) {
  return (
    <div>
      <p className="text-sm text-muted" style={{ marginBottom: '0.2rem' }}>{label}</p>
      <p style={{ margin: 0, fontWeight: 500 }}>{value}</p>
    </div>
  )
}

function nextLifecycleEvent(member) {
  if (!member.last_payment_at) return null
  const daysSince = Math.floor((Date.now() - new Date(member.last_payment_at).getTime()) / 86400000)
  if (member.status === 'active') {
    const d = 35 - daysSince
    return d > 0 ? `Lapses in ${d} day${d !== 1 ? 's' : ''}` : 'Lapse overdue'
  }
  if (member.status === 'lapsed') {
    const dClose  = 65 - daysSince
    const dRemind = 58 - daysSince
    if (dClose > 0) {
      if (dRemind > 0) return `Reminder due in ${dRemind}d · closes in ${dClose}d`
      return `Closes in ${dClose} day${dClose !== 1 ? 's' : ''}`
    }
    return 'Close overdue'
  }
  if (member.status === 'closed') return 'Closed'
  return null
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function MemberDetail({ memberId, onBack }) {
  const [member,       setMember]       = useState(null)
  const [transactions, setTransactions] = useState([])   // ascending, for running balance
  const [codes,        setCodes]        = useState([])
  const [emailLog,     setEmailLog]     = useState([])
  const [fraudAlerts,  setFraudAlerts]  = useState([])
  const [loading,      setLoading]      = useState(true)
  const [error,        setError]        = useState(null)

  const [creditAmount,  setCreditAmount]  = useState('')
  const [creditDesc,    setCreditDesc]    = useState('Manual credit adjustment')
  const [creditWorking, setCreditWorking] = useState(false)
  const [creditMsg,     setCreditMsg]     = useState(null)

  const [working,      setWorking]      = useState(false)
  const [closing,      setClosing]      = useState(false)
  const [actionError,  setActionError]  = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    const [memberRes, txRes, codesRes, emailRes, fraudRes] = await Promise.all([
      supabase.from('members').select('*').eq('id', memberId).single(),
      supabase.from('credit_transactions').select('*').eq('member_id', memberId).order('created_at', { ascending: true }),
      supabase.from('discount_codes').select('*').eq('member_id', memberId).order('created_at', { ascending: false }),
      supabase.from('email_log').select('*').eq('member_id', memberId).order('created_at', { ascending: false }),
      supabase.from('fraud_alerts').select('*').eq('member_id', memberId).order('created_at', { ascending: false }),
    ])
    if (memberRes.error) { setError(memberRes.error.message); setLoading(false); return }
    setMember(memberRes.data)
    setTransactions(txRes.data || [])
    setCodes(codesRes.data || [])
    setEmailLog(emailRes.data || [])
    setFraudAlerts(fraudRes.data || [])
    setLoading(false)
  }, [memberId])

  useEffect(() => { load() }, [load])

  // Running balance: accumulate oldest→newest, display newest first
  const txWithBalance = useMemo(() => {
    let running = 0
    return transactions.map(tx => {
      running += tx.amount
      return { ...tx, runningBalance: running }
    }).reverse()
  }, [transactions])

  async function handleAddCredit(e) {
    e.preventDefault()
    const amt = parseFloat(creditAmount)
    if (!amt || amt <= 0) { setCreditMsg({ ok: false, text: 'Enter a valid amount.' }); return }
    setCreditWorking(true)
    setCreditMsg(null)
    const { error: rpcErr } = await supabase.rpc('admin_add_credit', {
      p_member_id:   memberId,
      p_amount:      amt,
      p_description: creditDesc || 'Credit added by admin',
    })
    if (rpcErr) {
      setCreditMsg({ ok: false, text: rpcErr.message })
    } else {
      setCreditMsg({ ok: true, text: `${formatAUD(amt)} added.` })
      setCreditAmount('')
      load()
    }
    setCreditWorking(false)
  }

  async function handleReinstate() {
    setWorking(true)
    setActionError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch('/.netlify/functions/admin-api', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session.access_token}` },
        body:    JSON.stringify({ action: 'reinstate_member', member_id: memberId }),
      })
      const json = await res.json()
      if (!res.ok) throw new Error(json.error || 'Failed to reinstate member')
      load()
    } catch (err) {
      setActionError(err.message)
    }
    setWorking(false)
  }

  async function handleClose() {
    setWorking(true)
    setClosing(false)
    setActionError(null)
    const { error: rpcErr } = await supabase.rpc('close_member_account', { p_member_id: memberId })
    if (rpcErr) setActionError(rpcErr.message)
    else load()
    setWorking(false)
  }

  if (loading) return (
    <div style={{ padding: '3rem', textAlign: 'center' }}>
      <div className="spinner" style={{ margin: '0 auto' }} />
    </div>
  )
  if (error && !member) return (
    <div className="alert alert-error" style={{ margin: '2rem' }}>{error}</div>
  )
  if (!member) return null

  const daysSince = member.last_payment_at
    ? Math.floor((Date.now() - new Date(member.last_payment_at).getTime()) / 86400000)
    : null

  const BADGES = {
    active:    { cls: 'active',    label: 'ACTIVE'     },
    lapsed:    { cls: 'lapsed',    label: 'EXPIRING'   },
    closed:    { cls: 'closed',    label: 'CLOSED'     },
    suspended: { cls: 'suspended', label: 'SUSPENDED'  },
  }
  const badge = BADGES[member.status] || { cls: 'inactive', label: member.status || '—' }
  const evt   = nextLifecycleEvent(member)

  return (
    <div>

      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <button className="btn btn-secondary btn-sm" onClick={onBack}>← Back</button>
        <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600 }}>
          {member.name || member.email}
        </h2>
        <span className={`status-badge dot ${badge.cls}`}>{badge.label}</span>
        <button className="btn btn-secondary btn-sm" onClick={load} style={{ marginLeft: 'auto' }}>
          Refresh
        </button>
      </div>

      {/* ── Section 1: Member Info ────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title">Member Info</p>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
          gap: '1rem',
          marginTop: '0.75rem',
        }}>
          <InfoItem label="Name"  value={member.name || '—'} />
          <InfoItem label="Email" value={member.email} />
          <InfoItem label="Joined" value={formatDate(member.created_at)} />
          <InfoItem label="Balance" value={
            <strong style={{ fontSize: '1.1rem' }}>{formatAUD(member.credit_balance)}</strong>
          } />
          <InfoItem label="Last Payment" value={
            member.last_payment_at
              ? formatDate(member.last_payment_at)
              : <span style={{ color: '#D97706' }}>Never recorded</span>
          } />
          <InfoItem label="Days Since Payment" value={daysSince !== null ? `${daysSince} days` : '—'} />
          {evt && (
            <InfoItem
              label="Next Lifecycle Event"
              value={
                <span style={{
                  color: member.status === 'lapsed' ? '#D97706'
                       : member.status === 'closed' ? 'var(--muted)'
                       : 'inherit'
                }}>
                  {evt}
                </span>
              }
            />
          )}
        </div>
      </div>

      {/* ── Section 2: Credit Transactions ───────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title">Credit Transactions ({txWithBalance.length})</p>
        {txWithBalance.length === 0 ? (
          <div className="empty-state">No transactions.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th className="text-right">Amount</th>
                  <th className="text-right">Running Balance</th>
                </tr>
              </thead>
              <tbody>
                {txWithBalance.map(tx => {
                  const isPos = tx.amount > 0
                  const isNeg = tx.amount < 0
                  const cls   = isPos ? 'amount-positive' : isNeg ? 'amount-negative' : 'amount-neutral'
                  return (
                    <tr key={tx.id}>
                      <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(tx.created_at)}</td>
                      <td><span className={`type-badge ${tx.type}`}>{tx.type}</span></td>
                      <td className="text-sm">{tx.description || '—'}</td>
                      <td className={`text-right ${cls}`}>
                        {tx.amount === 0 ? '—' : `${isPos ? '+' : ''}${formatAUD(Math.abs(tx.amount))}`}
                      </td>
                      <td className="text-right" style={{ fontWeight: 600 }}>{formatAUD(tx.runningBalance)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Section 3: Denomination Codes ────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title">Denomination Codes ({codes.length})</p>
        {codes.length === 0 ? (
          <div className="empty-state">No codes generated.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Generated</th>
                  <th className="text-right">Amount</th>
                  <th>Code</th>
                  <th>Status</th>
                  <th>Redeemed At</th>
                  <th>Order ID</th>
                </tr>
              </thead>
              <tbody>
                {codes.map(c => {
                  const isActive = c.status === 'pending' && new Date(c.expires_at) > new Date()
                  return (
                    <tr key={c.id}>
                      <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(c.created_at)}</td>
                      <td className="text-right" style={{ fontWeight: 600 }}>{formatAUD(c.amount)}</td>
                      <td>
                        {isActive
                          ? <span className="code-inline">{c.code}</span>
                          : <span style={{ color: 'var(--muted)', fontStyle: 'italic', fontSize: '0.85rem' }}>
                              {c.code.slice(0, 3)}•••••
                            </span>
                        }
                      </td>
                      <td><span className={`type-badge ${c.status}`}>{c.status}</span></td>
                      <td className="text-sm text-muted">{c.redeemed_at ? formatDate(c.redeemed_at) : '—'}</td>
                      <td className="text-sm">
                        {c.order_id
                          ? <span className="code-inline" style={{ fontSize: '0.73rem' }}>{c.order_id}</span>
                          : '—'
                        }
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Section 4: Email History ──────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title">Email History ({emailLog.length})</p>
        {emailLog.length === 0 ? (
          <div className="empty-state">No emails logged.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Subject</th>
                  <th>Sent</th>
                  <th>Delivery</th>
                </tr>
              </thead>
              <tbody>
                {emailLog.map(e => {
                  const isBad        = e.delivery_status === 'bounced' || e.delivery_status === 'complained'
                  const isBackfilled = e.metadata?.backfilled
                  return (
                    <tr key={e.id}>
                      <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(e.created_at)}</td>
                      <td>
                        <span className="type-badge earned" style={{ textTransform: 'none', letterSpacing: 0 }}>
                          {e.email_type}
                        </span>
                        {isBackfilled && (
                          <span className="text-sm text-muted" style={{ marginLeft: '0.3rem', fontStyle: 'italic' }}>
                            historical
                          </span>
                        )}
                      </td>
                      <td className="text-sm">{e.subject}</td>
                      <td>
                        <span style={{
                          color:      e.sent_status === 'sent' ? '#2d7a3a' : 'var(--danger)',
                          fontWeight: 600,
                          fontSize:   '0.85rem',
                        }}>
                          {e.sent_status}
                        </span>
                      </td>
                      <td>
                        {e.delivery_status ? (
                          <span style={{
                            color:      isBad ? 'var(--danger)' : '#2d7a3a',
                            fontWeight: 600,
                            fontSize:   '0.85rem',
                          }}>
                            {e.delivery_status}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--muted)', fontSize: '0.85rem' }}>pending</span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Section 5: Fraud Flags ────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '1.25rem' }}>
        <p className="card-title" style={{
          color: fraudAlerts.some(a => !a.resolved) ? 'var(--danger)' : 'inherit'
        }}>
          Fraud Flags ({fraudAlerts.length})
        </p>
        {fraudAlerts.length === 0 ? (
          <div className="empty-state">No fraud flags.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Code</th>
                  <th className="text-right">Code Value</th>
                  <th className="text-right">Balance at Time</th>
                  <th>Order ID</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {fraudAlerts.map(a => (
                  <tr key={a.id} style={{ opacity: a.resolved ? 0.65 : 1 }}>
                    <td className="text-sm text-muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(a.created_at)}</td>
                    <td><span className="code-inline">{a.code}</span></td>
                    <td className="text-right" style={{ fontWeight: 600 }}>{formatAUD(a.code_amount)}</td>
                    <td className="text-right" style={{
                      color:      a.resolved ? 'inherit' : 'var(--danger)',
                      fontWeight: 600,
                    }}>
                      {formatAUD(a.actual_balance)}
                    </td>
                    <td className="text-sm">
                      {a.order_id
                        ? <span className="code-inline" style={{ fontSize: '0.73rem' }}>{a.order_id}</span>
                        : '—'
                      }
                    </td>
                    <td>
                      {a.resolved
                        ? <span style={{ color: '#2d7a3a', fontSize: '0.85rem', fontWeight: 600 }}>✓ Resolved</span>
                        : <span style={{ color: 'var(--danger)', fontSize: '0.85rem', fontWeight: 600 }}>Open</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Section 6: Admin Actions ──────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <p className="card-title">Admin Actions</p>
        <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', marginTop: '0.75rem' }}>

          {/* Add Credit */}
          <div style={{ flex: '1 1 280px' }}>
            <p className="text-sm" style={{ fontWeight: 600, marginBottom: '0.6rem' }}>Add Credit</p>
            {creditMsg && (
              <p style={{
                fontSize:     '0.85rem',
                color:        creditMsg.ok ? '#2d7a3a' : 'var(--danger)',
                marginBottom: '0.5rem',
              }}>
                {creditMsg.text}
              </p>
            )}
            <form onSubmit={handleAddCredit} style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
              <input
                type="number"
                className="form-input"
                placeholder="Amount (AUD)"
                value={creditAmount}
                onChange={e => setCreditAmount(e.target.value)}
                min="0.01"
                step="0.01"
                required
              />
              <input
                type="text"
                className="form-input"
                placeholder="Description"
                value={creditDesc}
                onChange={e => setCreditDesc(e.target.value)}
                maxLength={200}
              />
              <div>
                <button type="submit" className="btn btn-success btn-sm" disabled={creditWorking}>
                  {creditWorking ? 'Adding…' : 'Add Credit'}
                </button>
              </div>
            </form>
          </div>

          {/* Status Actions */}
          {(member.status === 'lapsed' || member.status === 'active') && (
            <div style={{ flex: '1 1 220px' }}>
              <p className="text-sm" style={{ fontWeight: 600, marginBottom: '0.6rem' }}>Account Status</p>
              {actionError && (
                <p style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: '0.5rem' }}>{actionError}</p>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', alignItems: 'flex-start' }}>
                {member.status === 'lapsed' && (
                  <button className="btn btn-success btn-sm" disabled={working} onClick={handleReinstate}>
                    {working ? 'Working…' : 'Reinstate Member'}
                  </button>
                )}
                {!closing ? (
                  <button className="btn btn-danger btn-sm" disabled={working} onClick={() => setClosing(true)}>
                    Close Account
                  </button>
                ) : (
                  <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className="text-sm" style={{ color: 'var(--danger)', fontWeight: 600 }}>
                      Expire {formatAUD(member.credit_balance)} and close?
                    </span>
                    <button className="btn btn-danger btn-sm" disabled={working} onClick={handleClose}>
                      {working ? 'Closing…' : 'Confirm'}
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setClosing(false)}>Cancel</button>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>

    </div>
  )
}
